import React, { useRef } from "react";
import { useUser } from "../context/user-context";

interface OtpInputFieldProps {
  otp: string;
  setOtp: (otp: string) => void;
  input: number;
  shouldAutoFocus?: boolean;
}

const OtpPin: React.FC<OtpInputFieldProps> = ({
  otp,
  setOtp,
  input,
  shouldAutoFocus = true,
}) => {
  const { theme } = useUser();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number
  ) => {
    const val = e.target.value.replace(/[^0-9]/g, ""); // Only numbers
    if (!val) return;
    const otpArr = otp.split("");
    otpArr[idx] = val[val.length - 1]; // Only last digit if pasted multiple
    setOtp(otpArr.join("").padEnd(input, ""));
    // Move to next input
    if (idx < input - 1 && val) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === "Backspace") {
      if (otp[idx]) {
        // Clear current
        const otpArr = otp.split("");
        otpArr[idx] = "";
        setOtp(otpArr.join("").padEnd(input, ""));
      } else if (idx > 0) {
        // Move to previous
        inputsRef.current[idx - 1]?.focus();
      }
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (paste) {
      setOtp(paste.slice(0, input).padEnd(input, ""));
      // Focus last filled
      setTimeout(() => {
        const nextIdx = Math.min(paste.length, input - 1);
        inputsRef.current[nextIdx]?.focus();
      }, 0);
    }
    e.preventDefault();
  };

  // Styles
  const baseStyle =
    "w-full h-14 border-2 rounded-xl text-center font-bold text-lg transition-all outline-none";
  const darkStyle =
    "border-[#095B97] bg-[#1F2B36] text-white focus:border-[#0B84CB]";
  const lightStyle =
    "border-[#AEBCC6] bg-transparent text-gray-900 focus:border-[#0B84CB]";

  return (
    <div
      className={`flex ${
        input === 6 ? "w-full" : "w-4/5 md:w-3/5"
      } mx-auto flex-row mt-2 justify-center items-center gap-3`}
    >
      {Array.from({ length: input }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => (inputsRef.current[idx] = el)}
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          autoFocus={shouldAutoFocus && idx === 0}
          className={`${baseStyle} ${
            theme === "dark" ? darkStyle : lightStyle
          }`}
          value={otp[idx] || ""}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
};

export default OtpPin;
